import type { ReactNode } from "react";
import {
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  ListItemAvatar,
  Tooltip,
  Stack,
} from "@mui/material";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import LogoutIcon from "@mui/icons-material/Logout";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import { config, envConfig, routesConfig } from "config/app-config";
import { useCurrentPathRoute } from "hooks";
import { authenticationService, Authorization, useUser } from "@sdeapps/react-core";
import NavigationDrawer from "./NavigationDrawer";
import MenuEntry from "./MenuEntry";
import MenuActionEntry from "./MenuActionEntry";
import type { NavigationEntry } from "./models/NavigationEntry";
import { exportService } from "services";
import { grey } from "@mui/material/colors";
import UserAvatar from "components/UserAvatar";

const ENV_NAME: string =
  (envConfig.name ?? "").length > 0 && envConfig.name.toLocaleLowerCase() !== "prod"
    ? envConfig.name.toLocaleUpperCase()
    : "";

function logout(): void {
  void authenticationService.logout();
}

const menus: Array<NavigationEntry> = [
  { libelle: "Recherche", icon: <SearchIcon />, path: routesConfig.home.path },
  {
    libelle: "Nouveau",
    icon: <PersonAddAlt1Icon />,
    path: routesConfig.nouveauContactMetier.path,
  },
  { libelle: "Création en masse", icon: <GroupAddIcon />, path: routesConfig.saisieEnMasse.path },
];

interface NavigationMenuProps {
  isOpen: boolean;
  onOpen: (v: boolean) => void;
}

export function NavigationMenu({
  isOpen = false,
  onOpen,
}: Readonly<NavigationMenuProps>): ReactNode {
  const { user } = useUser();
  const currentPath = useCurrentPathRoute();
  function handleDrawer(): void {
    onOpen(!isOpen);
  }

  return (
    <NavigationDrawer variant="permanent" className={isOpen ? "open" : undefined} open={isOpen}>
      {/* Nom de l'application */}
      <Stack
        direction="row"
        alignItems="center"
        sx={{ m: 1, alignItems: "center", justifyContent: "flex-start" }}
        spacing={1}>
        <img
          src="/comet_logo_192x192_c.png"
          alt="logo"
          style={{
            transition: "all 0.3s ease",
            marginBottom: isOpen ? 1 : "12px",
            width: isOpen ? "60px" : "48px",
            height: isOpen ? "60px" : "48px",
          }}
        />
        <Typography
          textAlign="left"
          fontSize="2rem"
          sx={{ lineHeight: "27px", fontWeight: "bold", fontSize: "2rem !important" }}>
          Contacts
          <br />
          Métiers
        </Typography>
      </Stack>

      {ENV_NAME !== "" && (
        <Typography variant="body2" sx={{ textAlign: "center" }}>
          {ENV_NAME}
        </Typography>
      )}

      <List sx={{ display: "flex", flexDirection: "column", flex: "1 1 auto", pb: 0 }}>
        {/* Bouton d'ouverture / fermeture du menu */}
        <ListItem
          secondaryAction={
            <Tooltip title={isOpen ? "Réduire" : "Agrandir"} placement="right" enterDelay={600}>
              <IconButton onClick={handleDrawer} sx={{ mr: -0.5 }}>
                {isOpen ? <FirstPageIcon /> : <LastPageIcon />}
              </IconButton>
            </Tooltip>
          }>
          <ListItemText primary="Menu" sx={{ opacity: isOpen ? 1 : 0 }} />
        </ListItem>
        <Divider />

        {/* Menu de navigation */}
        {menus.map((navigationEntry) =>
          navigationEntry.roles !== undefined && navigationEntry.roles?.length > 0 ? (
            <Authorization key={navigationEntry.libelle} roles={navigationEntry.roles}>
              <MenuEntry
                navigationEntry={navigationEntry}
                isOpen={isOpen}
                currentPath={currentPath}
                disableIfNoPath
              />
            </Authorization>
          ) : (
            <MenuEntry
              key={navigationEntry.libelle}
              navigationEntry={navigationEntry}
              isOpen={isOpen}
              currentPath={currentPath}
              disableIfNoPath
            />
          )
        )}
        <MenuActionEntry
          navigationEntry={{
            icon: <CloudDownloadIcon />,
            libelle: "Export Permanence",
            onClick: async () => exportService.getExportPermanence("Xlsx"),
          }}
          isOpen={isOpen}
        />

        {/* Espace vide qui grandit pour avoir le reste en bas de page*/}
        <ListItem sx={{ flex: "1 1 auto" }} />

        {/* Avatar et nom de l'utilisateur permettant la déconnexion */}
        <ListItem disablePadding sx={{ display: "block" }}>
          <Tooltip
            title="Déconnexion"
            placement="right"
            disableHoverListener={isOpen}
            enterDelay={0}>
            <ListItemButton onClick={logout}>
              <ListItemAvatar sx={{ ml: -0.5 }}>
                <UserAvatar />
              </ListItemAvatar>
              {isOpen && (
                <ListItemText
                  primary={
                    <Stack spacing={0.25}>
                      <Typography sx={{ whiteSpace: "normal", minWidth: "130px" }}>
                        {user.displayName}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                        <LogoutIcon color="error" sx={{ fontSize: "1.2rem" }} />
                        <Typography variant="body2" color="error">
                          Déconnexion
                        </Typography>
                      </Stack>
                    </Stack>
                  }
                />
              )}
            </ListItemButton>
          </Tooltip>
        </ListItem>

        {/* Version de build */}
        {isOpen && (
          <ListItem disablePadding sx={{ display: "block" }}>
            <Typography variant="body2" sx={{ color: grey[500], textAlign: "center" }}>
              build: {config.appVersion ?? "local"}
            </Typography>
          </ListItem>
        )}
      </List>
    </NavigationDrawer>
  );
}
